import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { TFormField } from '@nirmata/shared';
import { useEffect, useState } from 'react';

import { FieldLibrary } from './components/fields/FieldLibrary';
import { FORM_FIELDS_CONFIG } from './components/fields/fields.config';
import { FormCanvas } from './components/fields/FormCanvas';
import { FormPreview } from './components/fields/FormPreview';
import { BuilderLayout } from './components/layout/BuilderLayout';
import { PropertyEditor } from './components/property-editor/PropertyEditor';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { ThemeSelector } from './components/theme/ThemeSelector';
import { DropTarget, useBuilderStore } from './store/builder';
import { cn } from './utils/cn';

function findFieldById(field: TFormField | null, fieldId: string): TFormField | null {
  if (!field) return null;
  if (field.fieldId === fieldId) return field;
  if (field.childFields) {
    for (const child of field.childFields) {
      const found = findFieldById(child, fieldId);
      if (found) return found;
    }
  }
  return null;
}

function App() {
  const { addField, initializeContainer, selectedField, schema, updateField } = useBuilderStore();
  const [isDragging, setIsDragging] = useState(false);
  const [activeField, setActiveField] = useState<null | {
    id: string;
    type: string;
    label: string;
    defaultProps: Partial<TFormField>;
  }>(null);

  // Initialize container on mount
  useEffect(() => {
    initializeContainer();
  }, [initializeContainer]);

  // Use sensors to customize drag behavior
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require a more intentional drag with a minimum distance
      activationConstraint: {
        // Using a smaller distance to make drag more responsive
        distance: 3,
        // Add a delay to prevent accidental drags
        delay: 50,
      },
    })
  );

  const header = (
    <header className="p-4 flex items-center justify-between text-[var(--foreground)] bg-[var(--popover)] backdrop-blur-sm">
      <h1 className="text-xl font-medium tracking-tight">Nirmata: Builder</h1>
      <div className="flex items-center gap-4">
        <ThemeSelector />
      </div>
    </header>
  );

  const footer = (
    <footer className="px-8 py-3 text-center text-sm text-[var(--muted-foreground)] bg-[var(--background)] backdrop-blur-sm">
      Nirmata Form Builder &copy; {new Date().getFullYear()}
    </footer>
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setIsDragging(true);
    document.body.classList.add('is-dragging');

    if (active.data.current) {
      const { type, label, defaultProps } = active.data.current as {
        type: string;
        label: string;
        defaultProps: Partial<TFormField>;
      };
      setActiveField({
        id: active.id as string,
        type,
        label,
        defaultProps,
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Immediately hide the drag overlay
    setActiveField(null);

    if (over) {
      try {
        const { type, defaultProps } = active.data.current as {
          type: string;
          defaultProps: Partial<TFormField>;
        };

        const newField: TFormField = {
          fieldId: `${type}_${Date.now()}`,
          fieldName: `${type}_${Date.now()}`,
          componentType: defaultProps.componentType || 'text-field',
          ...defaultProps,
        };

        // Check for special drop targets
        if (over.id === 'form-canvas') {
          // Drop on the root canvas
          setTimeout(() => {
            addField(newField);
          }, 100);
        } else if (typeof over.id === 'string') {
          // Handle drops on specific targets
          const targetMatch = over.id.match(/^drop-(above|below|inside)-(.+)$/);

          if (targetMatch) {
            const [, position, targetField] = targetMatch;

            setTimeout(() => {
              addField(newField, targetField, position as DropTarget);
            }, 100);
          }
        }
      } catch (error) {
        console.error('Failed to add field:', error);
      }
    }

    // Reset state
    setIsDragging(false);
    document.body.classList.remove('is-dragging');
  };

  const handleDragCancel = () => {
    setActiveField(null);
    setIsDragging(false);
    document.body.classList.remove('is-dragging');
  };

  // Render the drag overlay content
  const renderDragOverlay = () => {
    if (!activeField) return null;

    // Find the field config to get the icon
    const fieldConfig = Object.values(FORM_FIELDS_CONFIG)
      .flat()
      .find((config) => config.type === activeField.type);
    const Icon = fieldConfig?.icon;

    return (
      <div
        className={cn(
          'cursor-grab border rounded-md py-2 px-3 shadow-sm border-[var(--border)] bg-[var(--popover)]',
          'hover:bg-[var(--muted)] hover:border-[var(--border)] transition-colors',
          'flex items-center gap-2'
        )}
      >
        {Icon && <Icon className="h-4 w-4 text-[var(--primary)] shrink-0" />}
        <div>
          <div className="font-medium">{activeField.label}</div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`bg-[var(--background)] text-[var(--foreground)] w-full h-full overflow-hidden relative ${isDragging ? 'is-dragging' : ''}`}
    >
      <ThemeProvider>
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          sensors={sensors}
          modifiers={[restrictToWindowEdges]}
        >
          <BuilderLayout
            header={header}
            footer={footer}
            sidebarContent={<FieldLibrary />}
            mainContent={<FormCanvas />}
            previewContent={
              selectedField && schema ? (
                <PropertyEditor
                  field={findFieldById(schema, selectedField)!}
                  updateField={
                    updateField as (
                      fieldId: string,
                      key: string,
                      value: string | Record<string, unknown> | unknown[]
                    ) => void
                  }
                />
              ) : (
                <FormPreview />
              )
            }
          />

          {/* Drag overlay for the active field */}
          <DragOverlay dropAnimation={null} zIndex={1000}>
            {activeField && renderDragOverlay()}
          </DragOverlay>
        </DndContext>
      </ThemeProvider>
    </div>
  );
}

export default App;
